/**
 * The activity graph (roadmap D240): one run, one agent's history, one task
 * or one conversation as a graph — the agents, the tools they called, the
 * commands they ran, the files they read and changed, the connectors, the
 * questions and approvals, the resets, hand-overs and verdicts.
 *
 * Presentational and portable: it takes the graph (shared/schemas/
 * activityGraph.ts) as a prop and imports nothing of the workbench's — no
 * host bridge, no app state — so the organisation's console carries the same
 * file. It draws on the full-screen stage's dark picture; the caller puts
 * `ActivityGraphPicture` in the stage, `ActivityGraphControls` in its panel
 * and `ActivityGraphDetail` in its side, all three over one
 * `useActivityGraphView` state.
 *
 * Keyboard: the picture is one tab stop; arrow keys move between nodes
 * (up and down a column, left and right across), Enter or Space opens the
 * node's detail. The search's results are buttons too, and a list of every
 * node is there for a screen reader.
 */
import './activityGraph.css';

import { ArrowDownToLine, Magnifier, Xmark } from '@gravity-ui/icons';
import { type KeyboardEvent, type ReactNode, useState } from 'react';

import type { ActivityNode } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import {
  CHROME,
  FILTER_GROUPS,
  type GraphTheme,
  inWindow,
  KIND_ONE,
  KIND_STYLE,
  kindColour,
  labelPlace,
  legendEntries,
  neighbours,
  nextNode,
  nodeSummary,
  shapePath,
  shortLabel,
  windowTimes,
} from './activityGraphModel.ts';
import type { ActivityGraphView } from './useActivityGraphView.ts';

const fmtTime = (ms: number): string => dateTime(ms, { date: 'medium', year: false });

/** The graph itself, drawn at the layout's own size (the stage scales and moves it). */
export function ActivityGraphPicture({ view, theme = 'dark' }: { view: ActivityGraphView; theme?: GraphTheme }) {
  const { layout, selected, matches, window } = view;
  const c = CHROME[theme];
  const lit = selected ? neighbours(layout, selected.id) : undefined;
  const found = new Set(matches.map((n) => n.id));
  const at = new Map(layout.nodes.map((p) => [p.node.id, p]));
  const tabStop = view.focused && at.has(view.focused) ? view.focused : (selected?.id ?? layout.nodes[0]?.node.id);
  const faint = (n: ActivityNode): boolean => (!!window && !inWindow(n, window)) || (!!lit && !lit.has(n.id)) || (found.size > 0 && !found.has(n.id) && !lit);

  const focusNode = (id: string) => {
    view.setFocused(id);
    requestAnimationFrame(() => document.querySelector<SVGGElement>(`[data-ag-node="${CSS.escape(id)}"]`)?.focus());
  };
  const onKey = (e: KeyboardEvent<SVGGElement>, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      view.select(view.selected?.id === id ? undefined : id);
      return;
    }
    const next = nextNode(layout, id, e.key);
    if (next) {
      e.preventDefault();
      e.stopPropagation();
      focusNode(next);
    }
  };

  return (
    <svg className="ag-picture" width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`} role="group" aria-label={`${view.graph.title}: ${layout.nodes.length} things`}>
      {layout.columns.map((col) => (
        <text key={col.x} x={col.x - 10} y={18} className="ag-column" fill={c.muted}>
          {col.label}
        </text>
      ))}
      <g aria-hidden>
        {layout.edges.map((e) => {
          const a = at.get(e.from)!;
          const b = at.get(e.to)!;
          const on = !!lit && lit.has(e.from) && lit.has(e.to) && (e.from === selected?.id || e.to === selected?.id);
          const dim = faint(a.node) || faint(b.node);
          return (
            <path
              key={`${e.from}→${e.to}→${e.kind}`}
              d={e.d}
              fill="none"
              stroke={on ? kindColour(b.node.kind === 'agent' ? a.node.kind : b.node.kind, theme) : c.edge}
              strokeWidth={on ? e.width + 0.5 : e.width}
              strokeDasharray={e.kind === 'sequence' ? '4 3' : undefined}
              opacity={on ? 0.95 : dim ? 0.08 : e.kind === 'touched' ? 0.35 : 0.7}
            />
          );
        })}
      </g>
      {layout.nodes.map((p) => {
        const n = p.node;
        const style = KIND_STYLE[n.kind];
        const colour = kindColour(n.kind, theme);
        const isSel = selected?.id === n.id;
        const isFound = found.has(n.id);
        const at = labelPlace(p);
        return (
          <g
            key={n.id}
            className="ag-node"
            data-ag-node={n.id}
            data-stage-no-drag
            role="button"
            tabIndex={n.id === tabStop ? 0 : -1}
            aria-label={nodeSummary(n)}
            aria-pressed={isSel}
            opacity={faint(n) ? 0.22 : 1}
            onClick={() => view.select(isSel ? undefined : n.id)}
            onKeyDown={(e) => onKey(e, n.id)}
            onFocus={() => view.setFocused(n.id)}
          >
            <title>{n.label}</title>
            {isSel || isFound ? <circle cx={p.x} cy={p.y} r={p.r + 5} fill="none" stroke={isSel ? c.ink : colour} strokeWidth={isSel ? 2 : 1.5} strokeDasharray={isSel ? undefined : '2 2'} /> : null}
            <circle className="ag-node__ring" cx={p.x} cy={p.y} r={p.r + 7} fill="none" />
            <path d={shapePath(style.shape, p.x, p.y, p.r)} fill={style.outline ? c.surface : colour} stroke={style.outline ? colour : c.surface} strokeWidth={style.outline ? 2 : 1.5} />
            <text x={at.x} y={at.y} textAnchor={at.anchor} className="ag-label" fill={c.ink} fontWeight={n.kind === 'agent' || n.kind === 'run' ? 600 : 400}>
              {shortLabel(n.label, at.chars)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** The panel's controls: the summary line, filters, the time scrubber, search, legend, export. */
export function ActivityGraphControls({ view, onExport, exporting, note }: { view: ActivityGraphView; onExport?: (format: 'png' | 'svg') => void; exporting?: boolean; note?: ReactNode }) {
  const { graph, layout, span, range } = view;
  const counts = new Map<string, number>();
  for (const n of graph.nodes) counts.set(n.kind, (counts.get(n.kind) ?? 0) + 1);
  const groupCount = (kinds: readonly string[]) => kinds.reduce((s, k) => s + (counts.get(k) ?? 0), 0);
  const win = windowTimes(span, range[0], range[1]);
  const legend = legendEntries(layout.nodes.map((p) => p.node));
  // Folded at first: the picture is the point, and the panel floats over its left edge.
  const [open, setOpen] = useState(false);
  return (
    <div className="ag-controls">
      <p className="m-0">
        {fmtTime(span.from)} – {fmtTime(span.to)} · {counts.get('agent') ?? 0} {counts.get('agent') === 1 ? 'agent' : 'agents'}
        {graph.truncated ? ' · the least-used things are folded away' : ''}
      </p>
      {note}

      <ul className="ag-legend" aria-label="Legend">
        {legend.map((l) => {
          const style = KIND_STYLE[l.kind];
          const colour = kindColour(l.kind, 'dark');
          return (
            <li key={l.kind}>
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                <path d={shapePath(style.shape, 7, 7, 5)} fill={style.outline ? 'transparent' : colour} stroke={colour} strokeWidth={style.outline ? 1.6 : 0} />
              </svg>
              {l.label}
            </li>
          );
        })}
      </ul>
      <button type="button" className="stage-link self-start" aria-expanded={open} onClick={() => setOpen(!open)} data-ag-more>
        {open ? 'Fewer controls' : 'Filter, find, pick a time, save as an image'}
      </button>
      {open ? (
        <>
          <div className="ag-row" role="group" aria-label="Show">
            {FILTER_GROUPS.map((g) => {
              const n = groupCount(g.kinds);
              if (!n) return null;
              const on = view.groups.has(g.id);
              return (
                <button key={g.id} type="button" className="stage-chip" data-on={on || undefined} aria-pressed={on} onClick={() => view.toggleGroup(g.id)}>
                  {g.label} <span className="tabular-nums">{n}</span>
                </button>
              );
            })}
          </div>

          <fieldset className="ag-scrub">
            <legend className="stage-faint">When</legend>
            <label className="ag-scrub__row">
              <span className="stage-faint">From</span>
              <input type="range" min={0} max={1000} value={Math.round(range[0] * 1000)} onChange={(e) => view.setRange([Math.min(Number(e.target.value) / 1000, range[1]), range[1]])} aria-valuetext={fmtTime(win.from)} />
            </label>
            <label className="ag-scrub__row">
              <span className="stage-faint">To</span>
              <input type="range" min={0} max={1000} value={Math.round(range[1] * 1000)} onChange={(e) => view.setRange([range[0], Math.max(Number(e.target.value) / 1000, range[0])])} aria-valuetext={fmtTime(win.to)} />
            </label>
            <span className="stage-faint tabular-nums" aria-live="polite">
              {view.window ? `${fmtTime(win.from)} – ${fmtTime(win.to)}; the rest is dimmed` : 'The whole time'}
              {view.window ? (
                <button type="button" className="stage-link ml-2" onClick={() => view.setRange([0, 1])}>
                  Whole time
                </button>
              ) : null}
            </span>
          </fieldset>

          <label className="ag-search">
            <Magnifier className="size-3.5 shrink-0" aria-hidden />
            <input type="search" placeholder="Find a tool, command or file" aria-label="Find a tool, command or file" value={view.query} onChange={(e) => view.setQuery(e.target.value)} />
            {view.query ? (
              <button type="button" className="stage-icon" aria-label="Clear the search" onClick={() => view.setQuery('')}>
                <Xmark className="size-3" />
              </button>
            ) : null}
          </label>
          {view.query.trim() ? (
            view.matches.length ? (
              <ul className="stage-list ag-matches" aria-label="Found">
                {view.matches.slice(0, 30).map((n) => (
                  <li key={n.id}>
                    <button type="button" className="stage-row" aria-pressed={view.selected?.id === n.id} onClick={() => view.select(n.id)} title={n.label}>
                      <span className="stage-dot" style={{ color: kindColour(n.kind, 'dark'), background: 'currentColor' }} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{n.label}</span>
                      <span className="stage-faint">{KIND_ONE[n.kind]}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="stage-faint m-0">Nothing by that name.</p>
            )
          ) : null}


          {onExport ? (
            <div className="ag-row">
              <button type="button" className="stage-btn" disabled={exporting} onClick={() => onExport('png')}>
                <ArrowDownToLine className="size-3.5" aria-hidden /> Save as PNG
              </button>
              <button type="button" className="stage-btn" disabled={exporting} onClick={() => onExport('svg')}>
                <ArrowDownToLine className="size-3.5" aria-hidden /> Save as SVG
              </button>
            </div>
          ) : null}

        </>
      ) : null}

      <ul className="sr-only" aria-label="Everything in the graph">
        {layout.nodes.map((p) => (
          <li key={p.node.id}>{nodeSummary(p.node)}</li>
        ))}
      </ul>
    </div>
  );
}

/** The selected node's detail, for the stage's side; `links` adds the caller's (the History link, Explore). */
export function ActivityGraphDetail({ node, onClose, links }: { node: ActivityNode; onClose: () => void; links?: ReactNode }) {
  const rows: Array<[string, ReactNode]> = [];
  rows.push(['What', KIND_ONE[node.kind]]);
  if (node.detail) rows.push(['Detail', node.detail]);
  if (node.kind !== 'run' && node.kind !== 'agent') rows.push(['Times', <span className="tabular-nums">{node.count}</span>]);
  if (node.tokens) rows.push(['Tokens used', <span className="tabular-nums">{node.tokens.toLocaleString()}</span>]);
  if (node.agent && node.kind !== 'agent') rows.push(['By', node.agent]);
  if (node.status) rows.push(['State', node.status]);
  if (node.firstAt) rows.push(['First', <span className="tabular-nums">{fmtTime(Date.parse(node.firstAt))}</span>]);
  if (node.lastAt && node.lastAt !== node.firstAt) rows.push(['Last', <span className="tabular-nums">{fmtTime(Date.parse(node.lastAt))}</span>]);
  return (
    <div className="ag-detail">
      <div className="stage-panel__head flex items-start gap-2">
        <h3 className="stage-panel__title m-0 min-w-0 flex-1 break-words">{node.label}</h3>
        <button type="button" className="stage-icon" aria-label="Close the detail" onClick={onClose}>
          <Xmark className="size-3.5" />
        </button>
      </div>
      <dl className="ag-detail__rows">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="stage-faint">{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {links ? <div className="stage-panel__foot">{links}</div> : null}
    </div>
  );
}
